import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import DownloadIcon from "@mui/icons-material/Download";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import { LoadingButton } from "@mui/lab";
import { saveAs } from "file-saver";
import axios from "axios";
import { toast } from "react-toastify";
import { toastError } from "../utils/util";
import { API_ENDPOINT, ONSITE_MODULE_ITEMS } from "../utils/constants";
import { getOnsiteHeader } from "../dashboard/onsitemodule/utils";
import { ScrollTableContainer } from "../components/visitors/Visitors";
import {
  printAll,
  printRow,
} from "../dashboard/onsitemodule/ImtexBadgeDimension";

export const EXHIBITOR_TEMPLATE =
  "https://s3.ap-south-1.amazonaws.com/expoplanner.in/staff_list+copy.xlsx";

const STAFF_BARCODE = "imtma-orgainser";

const normalizeHeader = (header) =>
  String(header || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const toBadge = (firstName, companyName) => ({
  barcode_id: STAFF_BARCODE,
  firstName: String(firstName || "").trim(),
  companyName: String(companyName || "").trim(),
});

function PrintStaffBadges() {
  const [excelData, setExcelData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [savingAction, setSavingAction] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setFileName(file.name);
    const reader = new FileReader();

    reader.onload = (loadEvent) => {
      try {
        const workbook = XLSX.read(loadEvent.target.result, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

        if (rows.length < 2) {
          setExcelData([]);
          toast.error("The uploaded spreadsheet does not contain any staff rows.");
          return;
        }

        const headers = rows[0].map(normalizeHeader);
        const nameIndex = headers.indexOf("name");
        const companyIndex = headers.indexOf("company name");

        if (nameIndex === -1 || companyIndex === -1) {
          setExcelData([]);
          toast.error('The spreadsheet must contain "Name" and "Company Name" columns.');
          return;
        }

        const badges = rows
          .slice(1)
          .map((row) => toBadge(row[nameIndex], row[companyIndex]))
          .filter((badge) => badge.firstName || badge.companyName);

        if (badges.some((badge) => !badge.firstName || !badge.companyName)) {
          setExcelData([]);
          toast.error("Every staff row must include both a name and company name.");
          return;
        }

        setExcelData(badges);
        if (!badges.length) toast.error("No staff details were found in the spreadsheet.");
      } catch (error) {
        setExcelData([]);
        toast.error("The spreadsheet could not be read. Please use the provided template.");
      } finally {
        setLoading(false);
      }
    };

    reader.onerror = () => {
      setExcelData([]);
      setLoading(false);
      toast.error("The selected file could not be read.");
    };
    reader.readAsBinaryString(file);
  };

  const saveAndPrint = async (badges, printType, rowIndex = null) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Please allow pop-ups to print staff badges.");
      return;
    }

    const action = printType === "bulk" ? "all" : `row-${rowIndex}`;
    setSavingAction(action);
    try {
      const { data } = await axios.post(
        `${API_ENDPOINT}/onsite/save-printed-staff-badges`,
        { badges, printType },
        getOnsiteHeader(ONSITE_MODULE_ITEMS.PRINT_STAFF_BADGE)
      );

      toast.success(data.message);
      if (printType === "bulk") {
        printAll(badges, printWindow);
      } else {
        printRow(badges[0], printWindow);
      }
    } catch (error) {
      printWindow.close();
      toastError(error);
    } finally {
      setSavingAction(null);
    }
  };

  const onCancel = () => {
    setExcelData([]);
    setFileName("");
  };

  return (
    <Box sx={{ py: 3 }}>
      <Container maxWidth="lg">
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Staff badge printing
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Import the staff list, review every badge, then save and print.
              </Typography>
            </Box>
            {excelData.length > 0 && (
              <Chip color="primary" label={`${excelData.length} badges ready`} />
            )}
          </Stack>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm="auto">
              <Button
                startIcon={<ImportExportIcon />}
                variant="contained"
                component="label"
                disabled={Boolean(savingAction)}
                fullWidth
              >
                Import Excel
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  hidden
                />
              </Button>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                startIcon={<DownloadIcon />}
                variant="outlined"
                fullWidth
                onClick={() => saveAs(EXHIBITOR_TEMPLATE, "staff_badge_template.xlsx")}
              >
                Download template
              </Button>
            </Grid>
          </Grid>

          {fileName && !loading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Previewing <strong>{fileName}</strong>. Confirm the names and companies before printing.
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
              <CircularProgress />
            </Box>
          ) : excelData.length === 0 ? (
            <Box sx={{ bgcolor: "grey.50", borderRadius: 2, p: { xs: 2, sm: 3 } }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Before uploading
              </Typography>
              <List dense disablePadding sx={{ mt: 1 }}>
                <ListItem disableGutters>
                  <ListItemText primary='Use the template columns "Name" and "Company Name".' />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="Include at least one staff member in the spreadsheet." />
                </ListItem>
              </List>
            </Box>
          ) : (
            <>
              <Typography variant="subtitle1" fontWeight={700}>
                Imported staff preview
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                A database record is created each time a badge is printed.
              </Typography>
              <Divider />

              <ScrollTableContainer>
                <Table aria-label="Imported staff badge preview">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {excelData.map((badge, index) => (
                      <TableRow key={`${badge.firstName}-${badge.companyName}-${index}`} hover>
                        <TableCell>{badge.firstName || "—"}</TableCell>
                        <TableCell>{badge.companyName || "—"}</TableCell>
                        <TableCell align="right">
                          <LoadingButton
                            onClick={() => saveAndPrint([badge], "single", index)}
                            loading={savingAction === `row-${index}`}
                            disabled={Boolean(savingAction) && savingAction !== `row-${index}`}
                            startIcon={<PrintRoundedIcon />}
                            variant="outlined"
                            size="small"
                          >
                            Save &amp; print
                          </LoadingButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollTableContainer>

              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                justifyContent="flex-end"
                spacing={1.5}
                sx={{ mt: 3 }}
              >
                <Button onClick={onCancel} variant="outlined" disabled={Boolean(savingAction)}>
                  Cancel
                </Button>
                <LoadingButton
                  onClick={() => saveAndPrint(excelData, "bulk")}
                  loading={savingAction === "all"}
                  disabled={Boolean(savingAction) && savingAction !== "all"}
                  startIcon={<PrintRoundedIcon />}
                  variant="contained"
                >
                  Save &amp; print all ({excelData.length})
                </LoadingButton>
              </Stack>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
}

export default PrintStaffBadges;
